import { useEffect, useMemo, useRef, useState } from "react"
import { Loader2, X } from "lucide-react"
import { toast } from "sonner"
import { GenerateWithModelLabel, type GenerateWithModelLabelHandle } from "@/components/media/generate-with-model-label"
import { MediaRecordGlyph } from "@/components/media/media-record-glyph"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { SelectDropdown } from "@/components/ui/select-dropdown"
import { VoicePicker, characterVoiceSelection } from "@/components/voices/voice-picker"
import { useAudioInputPicker } from "@/hooks/use-audio-input-picker"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"
import { useTranslation } from "@/hooks/use-translation"
import { buildAudioInputOptions, SYSTEM_AUDIO_INPUT_VALUE } from "@/lib/audio-inputs"
import { cn } from "@/lib/utils"
import type { VoiceSelection } from "@/lib/voices-api"
import type { TimelineLibraryVoiceRef, TimelineVoiceOption } from "./types"

export const AUDIO_COMPOSE_PLACEHOLDER_MS = 3000
/** TTS: поле текста должно быть удобным — форма не уже этого. */
const TTS_FORM_MIN_WIDTH_PX = 480

const MAX_RECORD_SECONDS = 300

/**
 * Единый размер всех контролов формы (box-border + border), чтобы высота
 * совпадала и без focus-ring. leading-tight — кириллические выносные не клипаются.
 */
const CTRL =
  "box-border h-7 min-h-7 max-h-7 rounded-md border border-ui-border bg-ui-bg text-xs leading-tight " +
  "focus:border-ui-accent focus:outline-none focus:ring-0 focus-visible:border-ui-accent focus-visible:ring-0"

const selectBtn = cn(CTRL, "min-w-0 px-2")

/** Закрытие: та же высота, без постоянного фона; лёгкий hover. */
const closeBtn =
  "box-border inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md " +
  "text-ui-text-secondary transition-colors hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:opacity-40"

const primaryBtn = cn(
  CTRL,
  "inline-flex shrink-0 items-center justify-center gap-1 border-ui-accent bg-ui-accent px-2 font-medium text-white",
  "hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40",
)

/** Слот REC/таймер: фиксированная ширина на idle, countdown и запись. */
const REC_SLOT_W = "box-border w-[4.75rem] shrink-0 justify-center tabular-nums"

/** Как у ClipView: на всю высоту дорожки (inset-y-0), без ring снаружи. */
const FORM_SHELL =
  "absolute inset-y-0 z-20 flex items-center overflow-visible rounded-md " +
  "border border-ui-accent bg-ui-bg-elevated select-none"

export type AudioComposeDraft = {
  trackId: string
  mode: "tts" | "record"
  atMs: number
  phase: "form" | "processing"
  assetId?: number
}

type Props = {
  draft: AudioComposeDraft
  pxPerMs: number
  projectId: number
  shotId: number
  voices: TimelineVoiceOption[]
  defaultLibraryVoice: TimelineLibraryVoiceRef | null
  stsProviderKinds: string[]
  busy: boolean
  onGenerateTts: (text: string, voice: VoiceSelection) => void
  onRecorded: (file: File, opts: { voice: VoiceSelection | null }) => void
  onClose: () => void
}

/** Сегмент-форма / плейсхолдер генерации на плейхеде custom_audio-дорожки. */
export function AudioComposeClip({
  draft,
  pxPerMs,
  projectId,
  shotId,
  voices,
  defaultLibraryVoice,
  stsProviderKinds,
  busy,
  onGenerateTts,
  onRecorded,
  onClose,
}: Props) {
  const leftPx = draft.atMs * pxPerMs
  const placeholderWidthPx = Math.max(AUDIO_COMPOSE_PLACEHOLDER_MS * pxPerMs, 6)
  // Record/STS: ширина по контенту (иначе справа от крестика пустота).
  // TTS: минимум под текстовое поле.
  const formWidth =
    draft.mode === "tts" ? Math.max(placeholderWidthPx, TTS_FORM_MIN_WIDTH_PX) : ("max-content" as const)

  if (draft.phase === "processing") {
    return (
      <div
        className="pointer-events-none absolute inset-y-0 z-20 flex items-center justify-center overflow-hidden rounded-md border border-ui-border-strong bg-ui-bg-elevated"
        style={{ left: leftPx, width: placeholderWidthPx }}
      >
        <Loader2 className="h-4 w-4 animate-spin text-ui-accent" />
      </div>
    )
  }

  return (
    <div className={FORM_SHELL} style={{ left: leftPx, width: formWidth }} onPointerDown={(e) => e.stopPropagation()}>
      {draft.mode === "tts" ? (
        <TtsComposeForm
          projectId={projectId}
          shotId={shotId}
          voices={voices}
          defaultLibraryVoice={defaultLibraryVoice}
          busy={busy}
          onGenerate={onGenerateTts}
          onClose={onClose}
        />
      ) : (
        <RecordComposeForm
          projectId={projectId}
          voices={voices}
          stsProviderKinds={stsProviderKinds}
          busy={busy}
          onRecorded={onRecorded}
          onClose={onClose}
        />
      )}
    </div>
  )
}

function TtsComposeForm({
  projectId,
  shotId,
  voices,
  defaultLibraryVoice,
  busy,
  onGenerate,
  onClose,
}: {
  projectId: number
  shotId: number
  voices: TimelineVoiceOption[]
  defaultLibraryVoice: TimelineLibraryVoiceRef | null
  busy: boolean
  onGenerate: (text: string, voice: VoiceSelection) => void
  onClose: () => void
}) {
  const { t } = useTranslation(["shot"])
  const [text, setText] = useState("")
  const generateActionRef = useRef<GenerateWithModelLabelHandle | null>(null)

  // Дефолт: первый пункт секции «Голоса героев» (реальный или синтетический
  // «по умолчанию»); героев нет вовсе — дефолтный библиотечный голос провайдера.
  const defaultVoice = useMemo<VoiceSelection | null>(() => {
    for (const row of voices) {
      const selection = characterVoiceSelection(row)
      if (selection) return selection
    }
    if (defaultLibraryVoice) {
      return {
        kind: "library",
        provider_kind: defaultLibraryVoice.provider_kind,
        provider_voice_id: defaultLibraryVoice.provider_voice_id,
      }
    }
    return null
  }, [voices, defaultLibraryVoice])
  const [voice, setVoice] = useState<VoiceSelection | null>(defaultVoice)
  useEffect(() => {
    setVoice((prev) => prev ?? defaultVoice)
  }, [defaultVoice])

  const trimmed = text.trim()
  const disabledReason =
    trimmed.length === 0 ? t("timeline.ttsPlaceholder") : voice == null ? t("timeline.voice") : undefined

  return (
    <div className="flex h-full w-full items-center gap-1 px-1">
      <input
        type="text"
        value={text}
        placeholder={t("timeline.ttsPlaceholder")}
        disabled={busy}
        // Форма открывается ради текста — печатать можно сразу, Enter запускает генерацию.
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            generateActionRef.current?.request()
          }
        }}
        onChange={(e) => setText(e.target.value)}
        className={cn(
          CTRL,
          "min-w-0 flex-[2] px-2 text-ui-text-primary placeholder:text-ui-text-muted disabled:opacity-40",
        )}
      />
      <VoicePicker
        projectId={projectId}
        value={voice}
        onChange={setVoice}
        characterVoices={voices}
        disabled={busy || (voices.length === 0 && !defaultLibraryVoice)}
        className="flex-1"
        buttonClassName={selectBtn}
        dropdownDirection="down"
        ariaLabel={t("timeline.voice")}
      />
      <GenerateWithModelLabel
        label={t("timeline.generate")}
        busyLabel={t("timeline.generate")}
        busy={busy}
        disabled={trimmed.length === 0 || voice == null}
        disabledReason={disabledReason}
        size="sm"
        buttonClassName="h-7 px-2"
        actionRef={generateActionRef}
        confirmDelayKey={confirmDelayKey("shot", shotId, "tts")}
        onClick={() => {
          if (!voice || !trimmed) return false
          onGenerate(trimmed, voice)
        }}
      />
      <button type="button" title={t("timeline.cancel")} disabled={busy} onClick={onClose} className={closeBtn}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function RecordComposeForm({
  projectId,
  voices,
  stsProviderKinds,
  busy,
  onRecorded,
  onClose,
}: {
  projectId: number
  voices: TimelineVoiceOption[]
  stsProviderKinds: string[]
  busy: boolean
  onRecorded: (file: File, opts: { voice: VoiceSelection | null }) => void
  onClose: () => void
}) {
  const { t } = useTranslation(["shot"])
  const picker = useAudioInputPicker({
    onDeviceUnavailable: () => toast.warning(t("timeline.microphoneUnavailable")),
  })
  // Голос замены: null = писать как есть (дефолт), иначе STS голосом героя или
  // библиотеки. STS умеет только ElevenLabs — провайдеров даёт сервер.
  const stsAvailable = stsProviderKinds.length > 0
  const [voice, setVoice] = useState<VoiceSelection | null>(null)

  const inputOptions = useMemo(() => {
    const listed = buildAudioInputOptions({
      devices: picker.devices,
      value: picker.value,
      pendingLabel: picker.pendingLabel,
      defaultDeviceId: picker.defaultDeviceId,
      labels: {
        defaultInput: t("timeline.defaultMicrophone"),
        defaultInputNamed: (name) => t("timeline.defaultInputNamed", { name }),
        microphoneN: (n) => t("timeline.microphoneN", { n }),
      },
    })
    return [
      ...listed.map((option, i) => ({ ...option, separatorAfter: i === listed.length - 1 })),
      // Не микрофон, а захват звука вкладки/приложения через диалог шаринга — отделяем.
      { value: SYSTEM_AUDIO_INPUT_VALUE, label: t("timeline.systemAudio") },
    ]
  }, [picker.devices, picker.value, picker.pendingLabel, picker.defaultDeviceId, t])

  const recorder = useAudioRecorder({
    minSeconds: 1,
    maxSeconds: MAX_RECORD_SECONDS,
    countdownSeconds: 3,
    source: picker.source,
    ...picker.recorderProps,
    onRecorded: (file) => onRecorded(file, { voice }),
    onTooShort: () => toast.error(t("timeline.recordingTooShort")),
    onPermissionDenied: (deniedSource) =>
      toast.error(
        deniedSource === "system" ? t("timeline.systemAudioCancelled") : t("timeline.recordingPermissionDenied"),
      ),
    onSystemAudioMissing: () => toast.error(t("timeline.systemAudioMissing")),
    onError: (message) => toast.error(message),
  })

  return (
    <div className="flex h-full w-max items-center gap-1 px-1">
      <SelectDropdown
        value={picker.value}
        onChange={picker.select}
        options={inputOptions}
        disabled={recorder.isBusy}
        fitContent
        buttonClassName={selectBtn}
        dropdownDirection="up"
        ariaLabel={t("timeline.audioInput")}
      />

      {stsAvailable ? (
        <VoicePicker
          projectId={projectId}
          value={voice}
          onChange={setVoice}
          characterVoices={voices}
          providerKinds={stsProviderKinds}
          noneOptionLabel={t("timeline.keepOriginalVoice")}
          disabled={recorder.isBusy}
          buttonClassName={cn(selectBtn, "w-max")}
          dropdownDirection="up"
          ariaLabel={t("timeline.voice")}
        />
      ) : null}

      {recorder.phase === "idle" ? (
        <button
          type="button"
          title={t("timeline.startRecording")}
          disabled={busy}
          onClick={() => void recorder.start()}
          className={cn(primaryBtn, REC_SLOT_W, "border-red-600 bg-red-600 hover:bg-red-500")}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MediaRecordGlyph recording={false} />}
          {t("timeline.rec")}
        </button>
      ) : recorder.phase === "countdown" ? (
        <span
          className={cn(
            CTRL,
            REC_SLOT_W,
            "inline-flex items-center px-2 text-center leading-none font-semibold text-red-400",
          )}
        >
          {recorder.countdownLeft}
        </span>
      ) : (
        <button
          type="button"
          title={t("timeline.stopRecording")}
          onClick={recorder.stop}
          className={cn(primaryBtn, REC_SLOT_W, "animate-pulse border-red-600 bg-red-600")}
        >
          <MediaRecordGlyph recording />
          {Math.floor(recorder.elapsedSeconds / 60)}:{String(recorder.elapsedSeconds % 60).padStart(2, "0")}
        </button>
      )}

      <button
        type="button"
        title={t("timeline.cancel")}
        onClick={() => {
          recorder.cancel()
          onClose()
        }}
        className={closeBtn}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
